import { format } from 'date-fns';

import { StringKey } from '../lang/index';
import { formatDividendAmount } from '../pages/dividends/utils/format-dividend-amount.util';
import { stakeholderTypeGroupTranslation } from '../translations/stakeholder-translation.util';
import { toRound } from '../utils/get-rounded-number.util';
import { getTranslation } from '../utils/get-translation.util';
import { Company } from './company.types';
import { Valuation } from './events.types';
import { ExerciseRequest } from './exercise-request.types';
import { SharePlan, SharePlanType, sharePlanTypeTitle } from './pool-plans.types';
import {
  Stakeholder,
  StakeholderType,
  StakeholderTypeGroup,
  stakeholderTypeTitle,
  TerminationCause,
  TerminationCauseTitle,
} from './stakeholder.types';
import { ApprovalVestingTask, TargetBasedVestingMilestoneItem } from './vesting-tasks.types';

export enum NotificationType {
  ACTIVITY = 'activity',
  REQUEST = 'request',
}

export const notificationTypeMap = Object.values(NotificationType);

export enum NotificationAction {
  ACCELERATION_DATE_7_DAYS = 'acceleration-date-7-days',
  ACCELERATION_DATE_REACHED = 'acceleration-date-reached',
  ACCESS_REVOKED = 'access-revoked',
  BULK_GRANT_UPLOAD = 'bulk-grant-upload',
  BULK_STAKEHOLDERS_UPLOAD = 'bulk-stakeholders-upload',
  DIVIDEND_PAYOUT_APPROVED = 'dividend-payout-approved',
  DIVIDEND_PAYOUT_CREATED = 'dividend-payout-created',
  DIVIDEND_PAYOUT_PAID_OUT = 'dividend-payout-paid-out',
  DIVIDEND_PAYOUT_STATUS_UPDATED = 'dividend-payout-status-updated',
  FINAL_DAY_TO_EXERCISE_REACHED = 'final-day-to-exercise-reached',
  GRANT_ACCELERATION_DATE = 'grant-acceleration-date',
  GRANT_ADDED = 'grant-added',
  //stakeholder:
  GRANT_EXERCISED = 'grant-exercised',
  GRANT_PAID_OUT = 'grant-paid-out',
  HURDLE_EXERCISED = 'hurdle-exercised',
  INVITATION_ACCEPTED = 'invitation-accepted',
  MILESTONE_APPROVED = 'milestone-approved',
  NEW_BUYBACK_RECORDED = 'new-buyback-recorded',
  NEW_CLASS_CONVERSION = 'new-class-conversion',
  NEW_FUNDRAISING_ROUND_ADDED = 'new-fundraising-round-added',
  NEW_GRANT_ISSUED = 'new-grant-issued',
  NEW_PLAN_ADDED = 'new-plan-added',
  NEW_POOL_ADDED = 'new-pool-added',
  NEW_SECONDARY_RECORDED = 'new-secondary-recorded',
  NEW_SHARE_CLASS_ADDED = 'new-share-class-added',
  NEW_SHARE_ISSUANCE = 'new-share-issuance',
  NEW_VALUATION_EVENT_ADDED = 'new-valuation-event-added',
  PHANTOM_SHARES_PAID_OUT = 'phantom-shares-paid-out',
  SAFE_CONVERTED = 'safe-converted',
  SAFE_MATURITY_DATE = 'safe-maturity-date',
  // RSA_EXERCISED = 'rsa-exercised',
  SAR_EXERCISED = 'sar-exercised',
  SHARE_CLASS_CONVERTED = 'share-class-converted',
  SHARES_VESTED = 'shares-vested',
  //admin/contributor:
  STAKEHOLDER_ADDED = 'stakeholder-added',
  STAKEHOLDER_DIVIDEND_PAYOUT_APPROVED = 'stakeholder-dividend-payout-approved',
  STAKEHOLDER_DIVIDEND_PAYOUT_PAID_OUT = 'stakeholder-dividend-payout-paid-out',
  STAKEHOLDER_DIVIDEND_PAYOUT_STATUS_UPDATED = 'stakeholder-dividend-payout-status-updated',
  STAKEHOLDER_LIMIT_REACHED = 'stakeholder-limit-reached',
  STAKEHOLDER_NEW_BUYBACK_RECORDED = 'stakeholder-new-buyback-recorded',
  STAKEHOLDER_NEW_SECONDARY_RECORDED = 'stakeholder-new-secondary-recorded',
  STAKEHOLDER_NEW_SHARE_ISSUANCE = 'stakeholder-new-share-issuance',
  STAKEHOLDER_ROLE_CHANGED = 'stakeholder-role-changed',
  STAKEHOLDER_SAFE_CONVERTED = 'stakeholder-safe-converted',
  STAKEHOLDER_TERMINATED = 'stakeholder-terminated',
  STOCK_OPTION_EXERCISED = 'stock-option-exercised',
  SUCCESSFUL_INVESTMENT = 'successful-investment',
  WARRANT_EXERCISED = 'warrant-exercised',
  WARRANT_EXPIRATION_DATE_REACHED = 'warrant-expiration-date-reached',
  WARRANT_PLAN_EXPIRATION_7_DAYS = 'warrant-plan-expiration-7-days',
}

export const notificationActionTitle: Record<NotificationAction, StringKey> = {
  [NotificationAction.STAKEHOLDER_ADDED]: 'NEW_STAKEHOLDER_ADDED',
  [NotificationAction.INVITATION_ACCEPTED]: 'INVITATION_ACCEPTED',
  [NotificationAction.STAKEHOLDER_TERMINATED]: 'STAKEHOLDER_TERMINATED',
  [NotificationAction.ACCESS_REVOKED]: 'ACCESS_REVOKED',
  [NotificationAction.STAKEHOLDER_ROLE_CHANGED]: 'STAKEHOLDER_ROLE_CHANGED',
  [NotificationAction.BULK_STAKEHOLDERS_UPLOAD]: 'BULK_STAKEHOLDERS_UPLOAD_NOTIFICATION',
  [NotificationAction.NEW_SHARE_CLASS_ADDED]: 'NEW_SHARE_CLASS_ADDED_NOTIFICATION',
  [NotificationAction.NEW_POOL_ADDED]: 'NEW_POOL_ADDED_NOTIFICATION',
  [NotificationAction.NEW_PLAN_ADDED]: 'NEW_PLAN_ADDED_NOTIFICATION',
  [NotificationAction.WARRANT_PLAN_EXPIRATION_7_DAYS]:
    'WARRANT_PLAN_EXPIRATION_7_DAYS_NOTIFICATION',
  [NotificationAction.WARRANT_EXPIRATION_DATE_REACHED]:
    'WARRANT_EXPIRATION_DATE_REACHED_NOTIFICATION',
  [NotificationAction.STOCK_OPTION_EXERCISED]: 'STOCK_OPTION_EXERCISED_NOTIFICATION',
  [NotificationAction.WARRANT_EXERCISED]: 'WARRANT_EXERCISED_NOTIFICATION',
  [NotificationAction.HURDLE_EXERCISED]: 'HURDLE_EXERCISED_NOTIFICATION',
  // [NotificationAction.RSA_EXERCISED]: "RSA_EXERCISED_NOTIFICATION",
  [NotificationAction.SAR_EXERCISED]: 'SAR_EXERCISED_NOTIFICATION',
  [NotificationAction.PHANTOM_SHARES_PAID_OUT]: 'PHANTOM_SHARES_PAID_OUT_NOTIFICATION',
  [NotificationAction.NEW_GRANT_ISSUED]: 'NEW_GRANT_ISSUED_NOTIFICATION',
  [NotificationAction.BULK_GRANT_UPLOAD]: 'BULK_GRANT_UPLOAD_NOTIFICATION',
  [NotificationAction.NEW_SECONDARY_RECORDED]: 'NEW_SECONDARY_RECORDED_NOTIFICATION',
  [NotificationAction.NEW_BUYBACK_RECORDED]: 'NEW_BUYBACK_RECORDED_NOTIFICATION',
  [NotificationAction.NEW_SHARE_ISSUANCE]: 'NEW_SHARE_ISSUANCE_NOTIFICATION',
  [NotificationAction.NEW_FUNDRAISING_ROUND_ADDED]: 'NEW_FUNDRAISING_ROUND_ADDED_NOTIFICATION',
  [NotificationAction.NEW_CLASS_CONVERSION]: 'NEW_CLASS_CONVERSION_NOTIFICATION',
  [NotificationAction.NEW_VALUATION_EVENT_ADDED]: 'NEW_VALUATION_EVENT_ADDED_NOTIFICATION',
  [NotificationAction.ACCELERATION_DATE_7_DAYS]: 'ACCELERATION_DATE_NOTIFICATION',
  [NotificationAction.ACCELERATION_DATE_REACHED]: 'ACCELERATION_DATE_NOTIFICATION',
  [NotificationAction.SAFE_MATURITY_DATE]: 'SAFE_MATURITY_DATE_NOTIFICATION',
  [NotificationAction.SAFE_CONVERTED]: 'SAFE_CONVERTED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_LIMIT_REACHED]: 'STAKEHOLDER_LIMIT_REACHED_NOTIFICATION',
  [NotificationAction.FINAL_DAY_TO_EXERCISE_REACHED]: 'FINAL_DAY_TO_EXERCISE',
  [NotificationAction.GRANT_EXERCISED]: 'GRANT_EXERCISED_NOTIFICATION',
  [NotificationAction.GRANT_PAID_OUT]: 'GRANT_PAID_OUT_NOTIFICATION',
  [NotificationAction.GRANT_ADDED]: 'GRANT_ADDED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_NEW_SECONDARY_RECORDED]:
    'STAKEHOLDER_NEW_SECONDARY_RECORDED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_NEW_BUYBACK_RECORDED]:
    'STAKEHOLDER_NEW_BUYBACK_RECORDED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_NEW_SHARE_ISSUANCE]:
    'STAKEHOLDER_NEW_SHARE_ISSUANCE_NOTIFICATION',
  [NotificationAction.SHARE_CLASS_CONVERTED]: 'SHARE_CLASS_CONVERTED_NOTIFICATION',
  [NotificationAction.SUCCESSFUL_INVESTMENT]: 'SUCCESSFUL_INVESTMENT_NOTIFICATION',
  [NotificationAction.SHARES_VESTED]: 'SHARES_VESTED_NOTIFICATION',
  [NotificationAction.MILESTONE_APPROVED]: 'MILESTONE_APPROVED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_SAFE_CONVERTED]: 'STAKEHOLDER_SAFE_CONVERTED_NOTIFICATION',
  [NotificationAction.GRANT_ACCELERATION_DATE]: 'GRANT_ACCELERATION_DATE_NOTIFICATION',
  [NotificationAction.DIVIDEND_PAYOUT_APPROVED]: 'DIVIDEND_PAYOUT_APPROVED_NOTIFICATION',
  [NotificationAction.DIVIDEND_PAYOUT_CREATED]: 'DIVIDEND_PAYOUT_CREATED_NOTIFICATION',
  [NotificationAction.DIVIDEND_PAYOUT_PAID_OUT]: 'DIVIDEND_PAYOUT_PAID_OUT_NOTIFICATION',
  [NotificationAction.DIVIDEND_PAYOUT_STATUS_UPDATED]:
    'DIVIDEND_PAYOUT_STATUS_UPDATED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_APPROVED]:
    'DIVIDEND_PAYOUT_APPROVED_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_PAID_OUT]:
    'DIVIDEND_PAYOUT_PAID_OUT_NOTIFICATION',
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_STATUS_UPDATED]:
    'DIVIDEND_PAYOUT_STATUS_UPDATED_NOTIFICATION',
};

export const notificationRequestTitle: Record<SharePlanType, StringKey> = {
  [SharePlanType.COMMON_STOCK]: 'EXERCISE_REQUESTED',
  [SharePlanType.GROWTH_HURDLE]: 'APPROVE_HURDLE_NOTIFICATION',
  [SharePlanType.WARRANTS]: 'EXERCISE_REQUESTED',
  [SharePlanType.BSPCE]: 'EXERCISE_REQUESTED',
  [SharePlanType.PHANTOM]: 'EXERCISE_REQUESTED',
  [SharePlanType.RSA]: 'EXERCISE_REQUESTED',
  [SharePlanType.RSU]: 'EXERCISE_REQUESTED',
  [SharePlanType.SAR]: 'EXERCISE_REQUESTED',
  [SharePlanType.STOCK_OPTIONS]: 'EXERCISE_REQUESTED',
};

export const renderMyHoldingButtonTypes = [
  NotificationAction.GRANT_EXERCISED,
  NotificationAction.GRANT_PAID_OUT,
  NotificationAction.SHARES_VESTED,
  NotificationAction.MILESTONE_APPROVED,
  NotificationAction.STAKEHOLDER_SAFE_CONVERTED,
  NotificationAction.GRANT_ACCELERATION_DATE,
  NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_APPROVED,
  NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_PAID_OUT,
  NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_STATUS_UPDATED,
];

export const renderViewPayoutButtonTypes = [
  NotificationAction.DIVIDEND_PAYOUT_APPROVED,
  NotificationAction.DIVIDEND_PAYOUT_PAID_OUT,
  NotificationAction.DIVIDEND_PAYOUT_STATUS_UPDATED,
  NotificationAction.DIVIDEND_PAYOUT_CREATED,
];

export type NotificationStakeholdersRead = {
  id: string;
  notification: Notification;
  notificationId: string;
  stakeholder: Stakeholder;
  stakeholderId: string;
};

export type ApproveRequest = {
  id: string;
  sharePlan: SharePlan;
  valuation: Valuation;
  vestingTasks: ApprovalVestingTask[];
};

export type AppNotification = {
  action?: NotificationAction;
  adminId?: string;
  adminName?: string;
  adminType?: StakeholderType;

  approveRequestId?: string;
  bulkCount?: number;
  company: Company;

  companyId: string;
  createdAt: Date;
  date?: string;
  dividendPayoutId?: string;
  eventName?: string;
  exerciseRequest?: ExerciseRequest;
  fromName?: string;
  grantId?: string;
  id: string;
  is_read: boolean;
  milestones?: TargetBasedVestingMilestoneItem[];
  notificationStakeholdersRead: NotificationStakeholdersRead[];
  numberOfGrants?: number;
  payout?: number;

  payoutIssuedShares?: number;
  percentage?: number;
  period?: string;
  preMoneyValuation?: number;
  requestHurdleValue?: number;
  requestId?: string;
  requestNextTriggerTime?: string;
  requestPlan?: string;
  requestPlanType?: SharePlanType;

  requestSharesCount?: number;
  requestTotalSharesCount?: number;
  resolved: boolean;
  sharePlanType?: SharePlanType;
  sharePrice?: number;
  sharesCount?: number;
  stakeholderId?: string;
  stakeholderIsTerminated?: boolean;
  stakeholderName?: string;
  stakeholderType?: StakeholderType;
  stakeholderTypeGroup?: StakeholderTypeGroup;
  targetBasedMilestoneRequest?: TargetBasedVestingMilestoneItem;
  terminationType?: TerminationCause;
  totalInvestment?: number;
  totalShares?: number;
  type: NotificationType;
};

type NotificationBaseOptions = {
  adminName: string;
  currencySymbol: string;
  isAdminYou: boolean;
  stakeholderName: string;
  stakeholderType: StakeholderType;
};

type ActiveNotificationBaseOptions = {
  action?: NotificationAction;
  bulkCount?: number;
  date?: string;
  eventName?: string;
  fromName?: string;
  grantId?: string;
  payout?: number;
  payoutIssuedShares?: number;
  percentage?: number;
  period?: string;
  preMoneyValuation?: number;
  sharePlanType?: SharePlanType;
  sharePrice?: number;
  sharesCount?: number;
  stakeholderTypeGroup?: StakeholderTypeGroup;
  terminationType?: TerminationCause;
  totalInvestment?: number;
  totalShares?: number;
} & NotificationBaseOptions;

type RequestNotificationBaseOptions = {
  hurdleValue: number;
  milestoneDate: string;
  milestoneName: string;
  planAvailableCount: number;
  planGrunts: number;
  planName: string;
  planType: SharePlanType;
  requestedSharesCount: number;
  stakeholderIsTerminated?: boolean;
} & NotificationBaseOptions;

const getAdminPart = ({ isAdminYou, adminName }: NotificationBaseOptions) =>
  isAdminYou
    ? `${getTranslation('BY').toLowerCase()} ${adminName} (${getTranslation('YOU')})`
    : `${getTranslation('BY').toLowerCase()} ${adminName}`;

const getCreatedByPart = ({ isAdminYou, adminName }: NotificationBaseOptions) =>
  isAdminYou
    ? `${getTranslation('CREATED_BY')}: ${adminName} (${getTranslation('YOU')})`
    : `${getTranslation('CREATED_BY')}: ${adminName}`;

const createStakeholderAddedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options.stakeholderName} ${getTranslation('WAS_ADDED_AS_A')} '${options.stakeholderType && getTranslation(stakeholderTypeTitle[options.stakeholderType])}' ${getAdminPart(options)}`;

const createInvitationAcceptedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options.stakeholderName} ${getTranslation('HAS_ACCEPTED_INVITATION')} ${getAdminPart(options)}`;

const createStakeholderTerminatedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options.stakeholderName} was Terminated ('${options.terminationType && getTranslation(TerminationCauseTitle[options.terminationType])}') as ${options.stakeholderType && getTranslation(stakeholderTypeTitle[options.stakeholderType])} ${getAdminPart(options)}`;

const createAccessRevokedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options.stakeholderName} has had access revoked ${getAdminPart(options)}`;

const createRoleChangedNotification = (_options: ActiveNotificationBaseOptions) =>
  `${getTranslation('PERMISSION_CHANGED_TO_CONTRIBUTOR')}`;

const createBulkStakeholderUploadNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.bulkCount} ${options?.stakeholderTypeGroup && getTranslation(stakeholderTypeGroupTranslation[options?.stakeholderTypeGroup])} ${options?.bulkCount && options?.bulkCount > 1 ? getTranslation('STAKEHOLDERS_ADDED') : getTranslation('STAKEHOLDER_ADDED').toLowerCase()} ${getAdminPart(options)}`;

const createNewShareClassNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('NEW_SHARE_CLASS')} "${options?.eventName}" ${getTranslation('HAS_BEEN_ADDED')} ${getAdminPart(options)}`;

const createNewPoolNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('THE_NEW_POOL')} "${options?.eventName}" ${getTranslation('HAS_BEEN_ADDED')} ${getAdminPart(options)}`;

const createNewPlanNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('THE_NEW_PLAN')} "${options?.eventName}" ${getTranslation('HAS_BEEN_ADDED')} ${getAdminPart(options)}`;

const createWarrantExpiration7DaysNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('WARRANT_PLAN')} "${options?.eventName}" ${getTranslation('EXPIRES_IN_7_DAYS')} ${options?.date && format(new Date(options.date), 'dd/MM/yyyy')}. ${getTranslation('TAKE_REQUIRED_ACTIONS')}`;

const createWarrantExpirationDateReachedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('EXPIRATION_DATE_FOR_WARRANT')} "${options?.eventName}" ${getTranslation('HAS_BEEN_REACHED_AS_OF')} ${options?.date && format(new Date(options.date), 'dd/MM/yyyy')}. ${getTranslation('ENSURE_TO_TAKE_ACTION')}`;

const createStockOptionExercisedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('STOCK_OPTION_EXERCISED_NOTIFICATION')} ${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('STOCK_OPTION_GRANTS')} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getTranslation('OF')} ${options.stakeholderName} ${getTranslation('HAVE_BEEN_EXERCISED')} ${getAdminPart(options)}`;

const createWarrantExercisedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('WARRANT')} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getTranslation('OF')} ${options.stakeholderName} ${getTranslation('HAVE_BEEN_EXERCISED')} ${getAdminPart(options)}`;

const createHurdleExercisedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('HURDLE_GRANTS')} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getTranslation('OF')} ${options.stakeholderName} ${getTranslation('HAVE_BEEN_EXERCISED')} ${getAdminPart(options)}`;

// const createRsaExercisedNotification = (options: ActiveNotificationBaseOptions) =>
//   `${options?.sharesCount?.toLocaleString('en-US')} RSA Grants (${options?.grantId?.split('-')[0].toUpperCase()}) of ${options.stakeholderName} ${getTranslation("HAVE_BEEN_EXERCISED")} ${getAdminPart(options)}`;

const createSarExercisedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('SARS')} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getTranslation('OF')} ${options.stakeholderName} ${getTranslation('HAVE_BEEN_EXERCISED')} ${getAdminPart(options)}. ${getTranslation('PAYOUT')}: ${options?.payout?.toLocaleString('en-US')}. ${getTranslation('ISSUED_SHARES')}: ${options?.payoutIssuedShares?.toLocaleString('en-US')}`;

const createPhantomPayedOutNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('PHANTOM_SHARES')} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getTranslation('OF')} ${options.stakeholderName} ${getTranslation('HAVE_BEEN_EXERCISED')} ${getAdminPart(options)}. ${getTranslation('PAYOUT')}: ${options?.payout?.toLocaleString('en-US')}. ${getTranslation('ISSUED_SHARES')}: ${options?.payoutIssuedShares?.toLocaleString('en-US')}`;

const createNewGrantNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('GRANTS_OF')} "${options?.eventName}" ${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])} ${getTranslation('HAVE_BEEN_GRANTED')}  ${options.stakeholderName} (${options?.grantId?.split('-')[0].toUpperCase()}) ${getAdminPart(options)}`;

const createBulkGrantUploadNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.bulkCount} ${getTranslation('GRANTS_ADDED')} ${getAdminPart(options)}`;

const createNewSecondaryNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SECONDARY_TRANSACTION_OF')} ${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('FROM')} ${options?.fromName} ${getTranslation('TO')} ${options?.stakeholderName} ${getTranslation('HAS_BEEN_RECORDED')} ${getAdminPart(options)}`;

const createNewBuyBackNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SHARE_BUYBACK_TRANSACTIONS_OF')} ${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('FROM')} ${options?.stakeholderName} ${getTranslation('RECORDED')} ${getAdminPart(options)}`;

const createNewShareIssuanceNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('HAVE_BEEN_ISSUED')} ${getTranslation('TO')} ${options?.stakeholderName} ${getAdminPart(options)}`;

const createNewFundraisingNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.bulkCount} ${getTranslation('INVESTMENTS_HAVE_BEEN_ADDED')} "${options?.eventName}", ${getTranslation('BRINGING_TOTAL_INVESTMENTS')} ${options?.totalInvestment?.toLocaleString('en-US')} ${options.currencySymbol}`;

const createNewClassConversionNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')}/${options?.totalShares?.toLocaleString('en-US')} ${getTranslation('SHARES_CONVERTED_FROM')} "${options?.fromName}" ${getTranslation('TO')} "${options?.eventName}" ${getTranslation('FOR')} ${options?.stakeholderName} ${getAdminPart(options)}. ${getTranslation('TOTAL_CONVERTED_SHARES')}: ${options?.sharesCount?.toLocaleString('en-US')}`;

const createNewValuationNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('NEW_VALUATION_EVENT')} "${options?.eventName}" ${getTranslation('HAS_BEEN_ADDED')} ${getAdminPart(options)} ${getTranslation('WITH_SHARE_PRICE')}: ${options?.sharePrice} ${options.currencySymbol} ${getTranslation('AND_PRE_MONEY_VALUATION')} ${options?.preMoneyValuation?.toLocaleString('en-US')} ${options.currencySymbol}`;

const createAccelerationDate7DaysNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('REMINDER_ACCELERATION')} "${options?.eventName}" ${getTranslation('SET_FOR')} ${options?.date && format(new Date(options.date), 'dd/MM/yyyy')}. ${getTranslation('NUMBER_OF_IMPACTED_STAKEHOLDERS')}: ${options?.bulkCount}`;

const createAccelerationDateReachedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('ACCELERATION_NOTIFICATION')} "${options?.eventName}" ${getTranslation('HAS_BEEN_TRIGGERED')}. ${getTranslation('STAKEHOLDERS_WITH_VESTED_GRANTS')}: ${options?.bulkCount}`;

const createSafeMaturityDateReachedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SAFE_MATURITY_DATE_FOR')} ${options?.stakeholderName} ${getTranslation('IS_REACHED')}. ${getTranslation('TAKE_REQUIRED_ACTIONS')}`;

const createSafeConvertedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SAFE_OF')} ${options?.stakeholderName} ${getTranslation('HAS_BEEN_CONVERTED')} ${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('SHARES').toLowerCase()}. ${getTranslation('TOTAL_PERCENT_OF_COMPANY_OWNED')}: ${toRound((options?.percentage || 0) * 100, 2)}.`;

const createStakeholderLimitReachedNotification = (_options: ActiveNotificationBaseOptions) =>
  `${getTranslation('STAKEHOLDER_LIMIT_REACHED_NOTIFICATION_TEXT')}`;

const createFinalDayToExerciseReachedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('REMINDER')}: ${getTranslation('TODAY')} (${format(options.date || new Date(), 'dd/MM/yyyy')}) is ${options?.stakeholderName}'s ${getTranslation('FINAL_DAY_TO_EXERCISE')} the grant (${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])}). ${getTranslation('TAKE_REQUIRED_ACTIONS')}`;

const createGrantExercisedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('GRANTS_ASSOCIATED')} "${options?.eventName}" (${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])}) ${getTranslation('HAVE_BEEN_EXERCISED')}. `;

const createStakeholderPhantomPayedOutNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.payout?.toLocaleString('en-US')} ${options.currencySymbol} ${getTranslation('ASSOCIATED_TO_PLAN')} "${options?.eventName}" (${getTranslation('PHANTOM')}) ${getTranslation('HAVE_BEEN_PAID_OUT')}. `;

const createGrantAddedStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOU_HAVE_BEEN_ISSUED')} ${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('GRANTS_UNDER_PLAN')} "${options?.eventName}" (${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])})`;

const createSecondaryStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SECONDARY_TRANSACTION_OF')} ${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('FROM')} ${options?.fromName} ${getTranslation('TO_YOU_HAS_BEEN_RECORDED')} ${getAdminPart(options)}`;

const createBuybackStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('SHARE_BUYBACK_TRANSACTIONS_OF')} ${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('IS_SUCCESSFUL')}`;

const createShareIssuanceStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} "${options?.eventName}" ${getTranslation('HAVE_BEEN_ISSUED_TO_YOU')} ${getAdminPart(options)}`;

const createShareClassConversionStakeholderNotification = (
  options: ActiveNotificationBaseOptions,
) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('OF')} "${options?.fromName}" ${getTranslation('HAVE_BEEN_SUCCESSFULLY_CONVERTED')} "${options?.eventName}"`;

const createInvestmentStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOUR_INVESTMENT_OF')} ${options?.totalInvestment?.toLocaleString('en-US')} ${options.currencySymbol} ${getTranslation('HAS_BEEN_RECORDED')}`;

const createTimeBasedVestedNotification = (options: ActiveNotificationBaseOptions) =>
  `${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('GRANTS_OF').toLowerCase()} "${options?.eventName}" (${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])}) ${getTranslation('HAVE_VESTED_NOTIFICATION')}`;

const createTargetBasedVestedNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('MILESTONE_NOTIFICATION')} "${options?.eventName}" ${getTranslation('MILESTONE_HAS_BEEN_APPROVED')}. ${getTranslation('AS_A_RESULT')} ${toRound(options?.percentage, 2)}% ${getTranslation('OF_YOUR')} ${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])} ${getTranslation('REPRESENTING')} ${options?.sharesCount?.toLocaleString('en-US')} ${getTranslation('SHARES').toLowerCase()} ${getTranslation('HAVE_VESTED_NOTIFICATION')}`;

const createSafeConvertedStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOUR_SAFE_CONVERTED')} ${options?.sharesCount?.toLocaleString('en-US')}. ${getTranslation('TOTAL_PERCENT_OF_COMPANY_OWNED')}: ${toRound((options?.percentage || 0) * 100, 2)}%.`;

const createAccelerationDateStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOUR_GRANT')} ${options?.grantId?.split('-')[0].toUpperCase()} ${getTranslation('OF')} "${options?.eventName}" (${options?.sharePlanType && getTranslation(sharePlanTypeTitle[options?.sharePlanType])}) ${getTranslation('GRANT_FULLY_VESTED_NOTIFICATION')}`;

const createExerciseRequestedNotification = (options: RequestNotificationBaseOptions) =>
  `${options.stakeholderName} ${options.stakeholderIsTerminated ? '(Terminated)' : ''} ${getTranslation('HAS_REQUESTED_EXERCISE')} ${options.requestedSharesCount?.toLocaleString('en-US')} ${options.planType && getTranslation(sharePlanTypeTitle[options.planType])} ${getTranslation('OUT_OF')} ${options.planAvailableCount} ${getTranslation('AVAILABLE').toLowerCase()}`;

const createHurdleApprovalNotification = (options: RequestNotificationBaseOptions) =>
  `${getTranslation('HURDLE_PLAN')} ${options.planName} ${getTranslation('HAS_EXCEEDED_HURDLE')} ${options.hurdleValue?.toLocaleString('en-US')}. ${options.planGrunts} ${getTranslation('GRANTS_PENDING_APPROVAL')}.`;

const createWarrantNotification = (options: RequestNotificationBaseOptions) =>
  `${options.stakeholderName} ${options.stakeholderIsTerminated ? '(Terminated)' : ''} ${getTranslation('HAS_REQUESTED_EXERCISE')} ${options.requestedSharesCount?.toLocaleString('en-US')} ${getTranslation('WARRANT')} ${getTranslation('OUT_OF')} ${options.planAvailableCount.toLocaleString('en-US')} ${getTranslation('AVAILABLE').toLowerCase()}.`;

const createTargetBasedVestingApprovalNotification = (options: RequestNotificationBaseOptions) =>
  `${getTranslation('MILESTONE')} ${options.milestoneName} ${getTranslation('UNDER_TARGET_BASED')} ${options.planName} (${options.planType && sharePlanTypeTitle[options.planType]}) ${getTranslation('HAS_SET_TARGET_DATE')} ${options.milestoneDate && format(options.milestoneDate, 'dd/MM/yyyy')}. ${getTranslation('WOULD_YOU_LIKE_APPROVE_TARGET')}`;

const dividendForPeriod = (options: ActiveNotificationBaseOptions) =>
  options?.period ? ` ${getTranslation('FOR')} ${options.period}` : '';

const dividendDatePart = (options: ActiveNotificationBaseOptions, labelKey: StringKey) =>
  options?.date
    ? ` ${getTranslation(labelKey)}: ${format(new Date(options.date), 'dd/MM/yyyy')}.`
    : '';

const dividendFromTransition = (options: ActiveNotificationBaseOptions) =>
  options?.fromName ? ` ${getTranslation('FROM')} ${options.fromName}` : '';

// The same two decimals the dividends screens and the status emails use. A bare toLocaleString
// defaults to three, so the Activity Center reported a different total than the page it links to.
const dividendTotal = (options: ActiveNotificationBaseOptions) =>
  options?.payout === undefined ? undefined : formatDividendAmount(options.payout);

const createDividendApprovedAdminNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_APPROVED')}. ${getTranslation('TOTAL_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}.${dividendDatePart(options, 'APPROVAL_DATE')} ${getAdminPart(options)}`;

const createDividendPaidOutAdminNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_MARKED_PAID_OUT')}. ${getTranslation('TOTAL_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}.${dividendDatePart(options, 'PAYOUT_DATE')} ${getAdminPart(options)}`;

const createDividendStatusUpdatedAdminNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('STATUS_OF_DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_UPDATED')}${dividendFromTransition(options)}. ${getTranslation('TOTAL_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol} ${getAdminPart(options)}`;

const createDividendCreatedAdminNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_CREATED_PENDING_APPROVAL')}. ${getTranslation('TOTAL_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}. ${getCreatedByPart(options)}.`;

const createDividendApprovedStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOUR_DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_APPROVED')}. ${getTranslation('YOUR_TOTAL_DIVIDEND_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}.${dividendDatePart(options, 'APPROVAL_DATE')}`;

const createDividendPaidOutStakeholderNotification = (options: ActiveNotificationBaseOptions) =>
  `${getTranslation('YOUR_DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_MARKED_PAID_OUT')}. ${getTranslation('YOUR_TOTAL_DIVIDEND_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}.${dividendDatePart(options, 'PAYOUT_DATE')}`;

const createDividendStatusUpdatedStakeholderNotification = (
  options: ActiveNotificationBaseOptions,
) =>
  `${getTranslation('STATUS_OF_YOUR_DIVIDEND_PAYOUT')} "${options?.eventName}"${dividendForPeriod(options)} ${getTranslation('HAS_BEEN_UPDATED')}${dividendFromTransition(options)}. ${getTranslation('YOUR_TOTAL_DIVIDEND_PAYOUT')}: ${dividendTotal(options)} ${options.currencySymbol}`;

const notificationHandlers: Record<
  NotificationAction,
  (options: ActiveNotificationBaseOptions) => string
> = {
  [NotificationAction.STAKEHOLDER_ADDED]: createStakeholderAddedNotification,
  [NotificationAction.INVITATION_ACCEPTED]: createInvitationAcceptedNotification,
  [NotificationAction.STAKEHOLDER_TERMINATED]: createStakeholderTerminatedNotification,
  [NotificationAction.STAKEHOLDER_ROLE_CHANGED]: createRoleChangedNotification,
  [NotificationAction.BULK_STAKEHOLDERS_UPLOAD]: createBulkStakeholderUploadNotification,
  [NotificationAction.NEW_SHARE_CLASS_ADDED]: createNewShareClassNotification,
  [NotificationAction.NEW_POOL_ADDED]: createNewPoolNotification,
  [NotificationAction.NEW_PLAN_ADDED]: createNewPlanNotification,
  [NotificationAction.WARRANT_PLAN_EXPIRATION_7_DAYS]: createWarrantExpiration7DaysNotification,
  [NotificationAction.WARRANT_EXPIRATION_DATE_REACHED]:
    createWarrantExpirationDateReachedNotification,
  [NotificationAction.STOCK_OPTION_EXERCISED]: createStockOptionExercisedNotification,
  [NotificationAction.WARRANT_EXERCISED]: createWarrantExercisedNotification,
  [NotificationAction.HURDLE_EXERCISED]: createHurdleExercisedNotification,
  // [NotificationAction.RSA_EXERCISED]: createRsaExercisedNotification,
  [NotificationAction.SAR_EXERCISED]: createSarExercisedNotification,
  [NotificationAction.PHANTOM_SHARES_PAID_OUT]: createPhantomPayedOutNotification,
  [NotificationAction.NEW_GRANT_ISSUED]: createNewGrantNotification,
  [NotificationAction.BULK_GRANT_UPLOAD]: createBulkGrantUploadNotification,
  [NotificationAction.NEW_SECONDARY_RECORDED]: createNewSecondaryNotification,
  [NotificationAction.NEW_BUYBACK_RECORDED]: createNewBuyBackNotification,
  [NotificationAction.NEW_SHARE_ISSUANCE]: createNewShareIssuanceNotification,
  [NotificationAction.NEW_FUNDRAISING_ROUND_ADDED]: createNewFundraisingNotification,
  [NotificationAction.NEW_CLASS_CONVERSION]: createNewClassConversionNotification,
  [NotificationAction.NEW_VALUATION_EVENT_ADDED]: createNewValuationNotification,
  [NotificationAction.ACCELERATION_DATE_7_DAYS]: createAccelerationDate7DaysNotification,
  [NotificationAction.ACCELERATION_DATE_REACHED]: createAccelerationDateReachedNotification,
  [NotificationAction.SAFE_MATURITY_DATE]: createSafeMaturityDateReachedNotification,
  [NotificationAction.SAFE_CONVERTED]: createSafeConvertedNotification,
  [NotificationAction.STAKEHOLDER_LIMIT_REACHED]: createStakeholderLimitReachedNotification,
  [NotificationAction.GRANT_EXERCISED]: createGrantExercisedNotification,
  [NotificationAction.GRANT_PAID_OUT]: createStakeholderPhantomPayedOutNotification,
  [NotificationAction.GRANT_ADDED]: createGrantAddedStakeholderNotification,
  [NotificationAction.STAKEHOLDER_NEW_SECONDARY_RECORDED]: createSecondaryStakeholderNotification,
  [NotificationAction.STAKEHOLDER_NEW_BUYBACK_RECORDED]: createBuybackStakeholderNotification,
  [NotificationAction.STAKEHOLDER_NEW_SHARE_ISSUANCE]: createShareIssuanceStakeholderNotification,
  [NotificationAction.SHARE_CLASS_CONVERTED]: createShareClassConversionStakeholderNotification,
  [NotificationAction.SUCCESSFUL_INVESTMENT]: createInvestmentStakeholderNotification,
  [NotificationAction.SHARES_VESTED]: createTimeBasedVestedNotification,
  [NotificationAction.MILESTONE_APPROVED]: createTargetBasedVestedNotification,
  [NotificationAction.STAKEHOLDER_SAFE_CONVERTED]: createSafeConvertedStakeholderNotification,
  [NotificationAction.GRANT_ACCELERATION_DATE]: createAccelerationDateStakeholderNotification,
  [NotificationAction.ACCESS_REVOKED]: createAccessRevokedNotification,
  [NotificationAction.FINAL_DAY_TO_EXERCISE_REACHED]: createFinalDayToExerciseReachedNotification,
  [NotificationAction.DIVIDEND_PAYOUT_APPROVED]: createDividendApprovedAdminNotification,
  [NotificationAction.DIVIDEND_PAYOUT_CREATED]: createDividendCreatedAdminNotification,
  [NotificationAction.DIVIDEND_PAYOUT_PAID_OUT]: createDividendPaidOutAdminNotification,
  [NotificationAction.DIVIDEND_PAYOUT_STATUS_UPDATED]: createDividendStatusUpdatedAdminNotification,
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_APPROVED]:
    createDividendApprovedStakeholderNotification,
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_PAID_OUT]:
    createDividendPaidOutStakeholderNotification,
  [NotificationAction.STAKEHOLDER_DIVIDEND_PAYOUT_STATUS_UPDATED]:
    createDividendStatusUpdatedStakeholderNotification,
};

const ActionNotificationFactory = {
  createActionNotification: (options: ActiveNotificationBaseOptions) => {
    const handler = options?.action && notificationHandlers[options.action];
    if (handler) {
      return handler(options);
    }
    return createAccessRevokedNotification(options);
  },
};
const RequestNotificationFactory = {
  createRequestNotification: (options: RequestNotificationBaseOptions) => {
    switch (options.planType) {
      case SharePlanType.BSPCE:
        return createExerciseRequestedNotification(options);
      case SharePlanType.GROWTH_HURDLE:
        return createHurdleApprovalNotification(options);
      case SharePlanType.SAR:
        return createExerciseRequestedNotification(options);
      case SharePlanType.STOCK_OPTIONS:
        return createExerciseRequestedNotification(options);
      case SharePlanType.WARRANTS:
        return createWarrantNotification(options);
      // case SharePlanType.RSA:
      //   return createExerciseRequestedNotification(options);
      default:
        return createTargetBasedVestingApprovalNotification(options);
    }
  },
};

export const activityNotificationBuilder = ActionNotificationFactory.createActionNotification;
export const requestNotificationBuilder = RequestNotificationFactory.createRequestNotification;
